import { useState, useEffect, useRef, useCallback } from "react";
import { CAT_WORDBANK } from "./services/CAT_WORDBANK";

// ── CAT SYSTEM PROMPT ─────────────────────────────────────────────────────────
// Built from the full wordbank + inspection PDF knowledge
function buildSystemPrompt(machine, fields, smellLog, chunks) {
  const failFields = fields.filter(f => f.status === "FAIL").map(f => `${f.label} (${f.id}): ${f.comment || "no comment"}`).join("\n");
  const monFields  = fields.filter(f => f.status === "MONITOR").map(f => `${f.label} (${f.id}): ${f.comment || "no comment"}`).join("\n");
  const passFields = fields.filter(f => f.status === "PASS").map(f => f.label).join(", ");
  const smellCtx   = smellLog.map(s => `[${s.time}]${s.autoDetected ? " AUTO-DETECTED" : ""} ${s.smells.map(x => x.label).join(", ")} — intensity ${s.intensity}%`).join("\n");
  const transcript = chunks.slice(-12).map(c => `[${c.time}] ${c.text}`).join("\n");

  // Build CAT terminology reference from wordbank
  const catTerms = CAT_WORDBANK.slice(0, 80).map(t => `${t.short} (${t.cat}): ${t.def}`).join("\n");

  return `You are CopyCat AI — the official inspection assistant for Caterpillar heavy equipment.
You have deep expertise in CAT machinery, field inspection protocols, fluid analysis (S·O·S), telematics (VisionLink/VIMS), and preventive maintenance.

━━━ ACTIVE MACHINE ━━━
Model: ${machine.fullName}
Unit ID: ${machine.id} | Asset ID: ${machine.assetId} | Serial: ${machine.serialNo}
Year: ${machine.year} | SMU Hours: ${machine.smuHours.toLocaleString()} hrs
Operator: ${machine.operatorName}
Inspection Form: ${machine.inspectionForm}
Machine Class: ${machine.machineClass} (${machine.category})

━━━ CURRENT INSPECTION RESULTS ━━━
FAIL ITEMS:
${failFields || "None recorded"}

MONITOR ITEMS:
${monFields || "None recorded"}

PASS ITEMS: ${passFields || "None recorded"}

━━━ SMELL / SMOKE ALERTS ━━━
${smellCtx || "No smell alerts logged"}

━━━ VOICE TRANSCRIPT (last 12 chunks) ━━━
${transcript || "No voice transcript yet"}

━━━ CAT TECHNICAL TERMINOLOGY ━━━
${catTerms}

━━━ INSPECTION KNOWLEDGE BASE ━━━
You know the CAT Safety & Maintenance Inspection checklists for:
- Hydraulic Excavators: 300.9-308 (Mini), 312-318 (GC), 320-352 (HC), 365-5230 (QM)
- Wheel Loaders: 950, 966, 972, 982 series
Key inspection areas: Ground (bucket/GET, undercarriage, boom/stick, carbody), Engine compartment (coolant, oil, hydraulic tank, radiator, hoses, belts, air filter, DEF), Cab (seat/seatbelt ≤3yr, ROPS, travel controls, alarms, HVAC)

━━━ BEHAVIOR RULES ━━━
- Always use proper CAT terminology (SMU, S·O·S, VIMS, GET, ROPS, DEF, etc.)
- Reference specific field IDs (G.1, E.3, C.4, 1.7, etc.) when discussing inspection items
- When asked for a summary, give a structured breakdown: CRITICAL → MONITOR → PASS
- For safety issues, always state urgency: IMMEDIATE ACTION / MONITOR / SCHEDULE SERVICE
- Be conversational but professional — like a senior CAT field technician
- Keep responses concise but complete — use bullet points for multi-item answers
- If an image is provided, analyze it as a CAT equipment inspection photo
- If a PDF is provided, extract and analyze inspection data from it
- Suggest follow-up S·O·S fluid analysis or PM intervals where relevant`;
}

// ── SUGGESTED PROMPTS ─────────────────────────────────────────────────────────
const SUGGESTED_PROMPTS = [
  { icon: "🔍", label: "Full inspection summary", text: "Give me a complete inspection summary with all findings organized by severity." },
  { icon: "⚠️", label: "Safety assessment", text: "Is this machine safe to operate right now? What are the critical safety concerns?" },
  { icon: "🔧", label: "Maintenance plan", text: "What maintenance actions are needed and in what order of priority?" },
  { icon: "💧", label: "Fluid analysis", text: "Based on the inspection findings, which fluid compartments should get S·O·S analysis?" },
  { icon: "📊", label: "Downtime estimate", text: "Estimate downtime if we address all FAIL items. What parts might be needed?" },
  { icon: "🚨", label: "Smell/smoke analysis", text: "Analyze the smell and smoke alerts detected. What do they indicate about machine health?" },
  { icon: "📋", label: "Inspection checklist gaps", text: "Which inspection items weren't covered in the transcript? What should the inspector check?" },
  { icon: "💰", label: "Cost impact", text: "What is the estimated cost impact of these findings if left unaddressed?" },
];

// ── LIVE WAVEFORM ─────────────────────────────────────────────────────────────
function ChatWaveform({ isRecording, isListening, audioLevel = 0 }) {
  const bars = 28;
  const timeRef = useRef(0);
  const [heights, setHeights] = useState(Array(bars).fill(2));
  const animRef = useRef(null);

  useEffect(() => {
    const tick = () => {
      timeRef.current += 0.08;
      const t = timeRef.current;
      setHeights(Array.from({ length: bars }, (_, i) => {
        if (!isRecording && !isListening) return 2;
        const base = Math.abs(Math.sin(i * 0.45 + t * 2.5));
        const mid  = Math.abs(Math.sin(i * 0.8 - t * 1.8)) * 0.6;
        const lvl  = isRecording ? (audioLevel / 100) * 0.8 + 0.2 : 0.4;
        return 2 + (base + mid) * lvl * 18;
      }));
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [isRecording, isListening, audioLevel]);

  const color = isRecording ? "#ef4444" : isListening ? "#FFCD11" : "#1e2530";
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, height: 32, padding: "0 4px" }}>
      {heights.map((h, i) => (
        <div key={i} style={{
          width: 2.5, borderRadius: 2,
          background: isRecording || isListening
            ? `linear-gradient(to top, ${color}99, ${color})`
            : "#1e2530",
          height: `${h}px`,
          transition: isRecording || isListening ? "height 0.08s ease" : "height 0.4s ease, background 0.3s",
          boxShadow: (isRecording || isListening) && h > 8 ? `0 0 4px ${color}60` : "none",
        }} />
      ))}
    </div>
  );
}

// ── MESSAGE BUBBLE ─────────────────────────────────────────────────────────────
function MessageBubble({ msg, isLatest }) {
  const isUser = msg.role === "user";
  const isSystem = msg.role === "system";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  if (isSystem) {
    return (
      <div style={{ textAlign: "center", padding: "6px 0", opacity: visible ? 1 : 0, transition: "opacity 0.3s" }}>
        <span style={{ fontSize: 7, color: "#374151", background: "#0d0d0d", padding: "2px 8px", borderRadius: 10, letterSpacing: 1 }}>{msg.content}</span>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: isUser ? "flex-end" : "flex-start",
      marginBottom: 12,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.25s ease, transform 0.25s ease",
    }}>
      <div style={{ fontSize: 6, color: "#374151", marginBottom: 3, letterSpacing: 1, display: "flex", alignItems: "center", gap: 4 }}>
        {!isUser && <span style={{ color: "#FFCD11", fontWeight: 700 }}>⬡ COPYCAT AI</span>}
        {isUser && <span style={{ color: "#4b5563" }}>YOU</span>}
        <span style={{ color: "#1e2530" }}>{msg.time}</span>
      </div>
      <div style={{
        maxWidth: "88%",
        padding: isUser ? "8px 12px" : "10px 12px",
        borderRadius: isUser ? "12px 12px 3px 12px" : "3px 12px 12px 12px",
        background: isUser ? "#1a1f2e" : "#0a0d12",
        border: `1px solid ${isUser ? "#2d3748" : "#1d4ed820"}`,
        fontSize: 9,
        color: isUser ? "#e2e8f0" : "#cbd5e1",
        lineHeight: 1.7,
        position: "relative",
        boxShadow: isLatest && !isUser ? "0 0 20px rgba(29,78,216,0.08)" : "none",
      }}>
        {!isUser && msg.typing ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center", padding: "2px 0" }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#FFCD11", animation: `blink 1.2s ${i*0.2}s infinite` }} />
            ))}
          </div>
        ) : (
          <div style={{ whiteSpace: "pre-wrap" }}>
            {msg.content.split("\n").map((line, li) => {
              // Render bold **text**
              const parts = line.split(/(\*\*[^*]+\*\*)/g);
              return (
                <div key={li} style={{ marginBottom: line === "" ? 4 : 0 }}>
                  {parts.map((p, pi) =>
                    p.startsWith("**") && p.endsWith("**")
                      ? <strong key={pi} style={{ color: "#FFCD11", fontWeight: 700 }}>{p.slice(2,-2)}</strong>
                      : p.startsWith("•") || p.startsWith("-")
                        ? <span key={pi} style={{ color: "#93c5fd" }}>{p}</span>
                        : <span key={pi}>{p}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* CAT term chips on AI messages */}
      {!isUser && !msg.typing && msg.catTerms && msg.catTerms.length > 0 && (
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 4, maxWidth: "88%" }}>
          {msg.catTerms.map((t, i) => (
            <span key={i} style={{ fontSize: 6, padding: "1px 5px", borderRadius: 2, background: "#0a1628", border: "1px solid #1d4ed8", color: "#60a5fa", letterSpacing: 0.5 }}>
              {t.short}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FILE PREVIEW ──────────────────────────────────────────────────────────────
function FilePreview({ file, onRemove }) {
  const isImage = file.type.startsWith("image/");
  const isPDF   = file.type === "application/pdf";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "#0d0d0d", border: "1px solid #1e2530", borderRadius: 6, marginBottom: 6 }}>
      <span style={{ fontSize: 16 }}>{isImage ? "🖼️" : isPDF ? "📄" : "📎"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 8, color: "#9ca3af", fontWeight: 600 }}>{file.name}</div>
        <div style={{ fontSize: 7, color: "#374151" }}>{(file.size / 1024).toFixed(1)} KB · {isImage ? "Image" : isPDF ? "PDF" : "File"}</div>
      </div>
      {isImage && file.preview && (
        <img src={file.preview} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4, border: "1px solid #1e2530" }} />
      )}
      <button onClick={onRemove} style={{ background: "none", border: "none", color: "#4b5563", cursor: "pointer", fontSize: 12, padding: 2, lineHeight: 1 }}>✕</button>
    </div>
  );
}

// ── MAIN CHAT COMPONENT ───────────────────────────────────────────────────────
export default function CopyCatChat({ machine, fields, smellLog, chunks, onClose }) {
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState("");
  const [loading, setLoading]           = useState(false);
  const [isRecording, setIsRecording]   = useState(false);
  const [audioLevel, setAudioLevel]     = useState(0);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [streamText, setStreamText]     = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [inputFocused, setInputFocused] = useState(false);

  const messagesEndRef  = useRef(null);
  const mediaRecRef     = useRef(null);
  const audioChunksRef  = useRef([]);
  const analyserRef     = useRef(null);
  const animFrameRef    = useRef(null);
  const fileInputRef    = useRef(null);
  const inputRef        = useRef(null);

  // ── Auto-scroll ─────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamText]);

  // ── Welcome message on mount ─────────────────────────────────────────
  useEffect(() => {
    const failCount = fields.filter(f => f.status === "FAIL").length;
    const monCount  = fields.filter(f => f.status === "MONITOR").length;
    setTimeout(() => {
      setMessages([{
        role: "assistant",
        content: `**CopyCat AI** online — connected to **${machine.id}** (${machine.fullName})\n\n${failCount > 0 ? `⚠ I can see **${failCount} FAIL** and ${monCount} MONITOR items in this inspection. ` : monCount > 0 ? `~ I see ${monCount} items to monitor. ` : "✓ No critical failures detected. "}Ask me anything about this machine, the inspection findings, maintenance needs, or upload a photo/PDF for analysis.\n\nYou can also tap the mic below to speak your question.`,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        catTerms: [{ short: "VIMS", cat: "TEL" }, { short: "S·O·S", cat: "SOS" }, { short: "SMU", cat: "TEL" }],
      }]);
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Extract CAT terms from response ─────────────────────────────────
  const extractTermsFromText = useCallback((text) => {
    const lower = text.toLowerCase();
    return CAT_WORDBANK.filter(t => lower.includes(t.term)).slice(0, 5);
  }, []);

  // ── Send message ─────────────────────────────────────────────────────
  const sendMessage = useCallback(async (overrideText = null, fileData = null) => {
    const text = (overrideText || input).trim();
    if (!text && attachedFiles.length === 0 && !fileData) return;
    if (loading) return;

    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    // Build user message content
    const userContent = text || (attachedFiles.length > 0 ? `[Attached: ${attachedFiles.map(f => f.name).join(", ")}]` : "");
    setMessages(prev => [...prev, { role: "user", content: userContent, time }]);
    setInput("");
    setShowSuggestions(false);
    setLoading(true);

    // Add typing indicator
    const typingId = Date.now();
    setMessages(prev => [...prev, { role: "assistant", content: "", time, typing: true, id: typingId }]);

    try {
      const systemPrompt = buildSystemPrompt(machine, fields, smellLog, chunks);

      // Build message history for API
      const history = messages.slice(-8).filter(m => !m.typing).map(m => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.content,
      }));

      // Build multimodal content
      const userMessageContent = [];
      if (text) userMessageContent.push({ type: "text", text });

      // Process attached images for vision
      const imageFiles = attachedFiles.filter(f => f.type.startsWith("image/"));
      for (const imgFile of imageFiles) {
        if (imgFile.base64) {
          userMessageContent.push({
            type: "image_url",
            image_url: { url: `data:${imgFile.type};base64,${imgFile.base64}`, detail: "high" }
          });
        }
      }

      // Process PDFs - extract text context
      const pdfFiles = attachedFiles.filter(f => f.type === "application/pdf");
      if (pdfFiles.length > 0) {
        userMessageContent.push({ type: "text", text: `\n[PDF attached: ${pdfFiles.map(f => f.name).join(", ")} — analyze as CAT inspection document]` });
      }

      if (userMessageContent.length === 0) userMessageContent.push({ type: "text", text: userContent });

      // Use gpt-4o for vision (images), gpt-4o-mini for text-only
      const hasImages = imageFiles.length > 0;
      const model = hasImages ? "gpt-4o" : "gpt-4o-mini";

      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model,
          max_tokens: 600,
          stream: false,
          messages: [
            { role: "system", content: systemPrompt },
            ...history,
            { role: "user", content: hasImages ? userMessageContent : (userMessageContent[0]?.text || userContent) },
          ],
        }),
      });

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content || "I couldn't process that. Please check your API key.";
      const catTerms = extractTermsFromText(reply);

      // Replace typing indicator with real message
      setMessages(prev => prev.map(m =>
        m.id === typingId
          ? { role: "assistant", content: reply, time, catTerms, id: typingId }
          : m
      ));

      // Clear attachments after send
      setAttachedFiles([]);

    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === typingId
          ? { role: "assistant", content: "Connection error — check your OpenAI API key in Vercel environment variables.", time }
          : m
      ));
    }
    setLoading(false);
  }, [input, loading, messages, attachedFiles, machine, fields, smellLog, chunks, extractTermsFromText]);

  // ── Voice recording ──────────────────────────────────────────────────
  const startVoiceRecord = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Live audio level monitor
      const dataArr = new Uint8Array(analyser.frequencyBinCount);
      const monitorLevel = () => {
        if (!isRecording) return;
        analyser.getByteFrequencyData(dataArr);
        const avg = dataArr.reduce((a, b) => a + b, 0) / dataArr.length;
        setAudioLevel(Math.min(100, avg * 1.5));
        animFrameRef.current = requestAnimationFrame(monitorLevel);
      };

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      recorder.ondataavailable = e => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        cancelAnimationFrame(animFrameRef.current);
        setAudioLevel(0);
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("file", blob, "voice.webm");
        formData.append("model", "whisper-1");

        try {
          const res = await fetch("https://api.openai.com/v1/audio/transcriptions", {
            method: "POST",
            headers: { "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}` },
            body: formData,
          });
          const data = await res.json();
          if (data.text?.trim()) {
            setInput(data.text.trim());
            // Auto-send after transcription
            setTimeout(() => sendMessage(data.text.trim()), 100);
          }
        } catch { setInput("Voice transcription failed. Check API key."); }
      };

      recorder.start();
      mediaRecRef.current = recorder;
      setIsRecording(true);
      monitorLevel();
    } catch { alert("Microphone access denied."); }
  }, [isRecording, sendMessage]);

  const stopVoiceRecord = useCallback(() => {
    mediaRecRef.current?.stop();
    setIsRecording(false);
  }, []);

  // ── File attachment ──────────────────────────────────────────────────
  const handleFileAttach = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        const preview = file.type.startsWith("image/") ? reader.result : null;
        setAttachedFiles(prev => [...prev, { name: file.name, type: file.type, size: file.size, base64, preview }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  }, []);

  // ── Suggested prompt click ────────────────────────────────────────────
  const handleSuggestion = useCallback((text) => {
    setInput(text);
    setShowSuggestions(false);
    setTimeout(() => sendMessage(text), 50);
  }, [sendMessage]);

  const catYellow = "#FFCD11";

  return (
    <div style={{ position: "absolute", inset: 0, background: "#070a0f", zIndex: 996, display: "flex", flexDirection: "column", fontFamily: "'IBM Plex Mono', monospace" }}>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid #0d1117", flexShrink: 0, background: "#0a0d12" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, background: catYellow, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 12px ${catYellow}40`, flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#fff", fontWeight: 700, letterSpacing: 1 }}>COPYCAT <span style={{ color: catYellow }}>AI</span></div>
              <div style={{ fontSize: 6, color: "#374151", letterSpacing: 1 }}>GPT-4o · {machine.id} · CAT WORDBANK LOADED</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 6px", borderRadius: 10, background: "#0a2016", border: "1px solid #14532d" }}>
              <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 4px #22c55e" }} />
              <span style={{ fontSize: 6, color: "#22c55e", letterSpacing: 1 }}>ONLINE</span>
            </div>
            <button onClick={onClose}
              style={{ background: "none", border: "1px solid #1e2530", color: "#6b7280", padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 8, fontFamily: "inherit", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#374151"; e.currentTarget.style.color = "#9ca3af"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e2530"; e.currentTarget.style.color = "#6b7280"; }}>
              ✕ CLOSE
            </button>
          </div>
        </div>

        {/* Machine context strip */}
        <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {fields.filter(f => f.status === "FAIL").length > 0 && (
            <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#200a0a", color: "#f87171", border: "1px solid #7f1d1d" }}>
              ✗ {fields.filter(f => f.status === "FAIL").length} FAIL
            </span>
          )}
          {fields.filter(f => f.status === "MONITOR").length > 0 && (
            <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#1f1500", color: "#fbbf24", border: "1px solid #92400e" }}>
              ~ {fields.filter(f => f.status === "MONITOR").length} MONITOR
            </span>
          )}
          {smellLog.length > 0 && (
            <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#200a0a", color: "#fca5a5", border: "1px solid #7f1d1d" }}>
              🔥 {smellLog.length} SMELL
            </span>
          )}
          <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#0a1628", color: "#60a5fa", border: "1px solid #1d4ed8" }}>
            {machine.smuHours.toLocaleString()} SMU HRS
          </span>
          <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#0a1628", color: "#60a5fa", border: "1px solid #1d4ed8" }}>
            {chunks.length} VOICE CHUNKS
          </span>
        </div>
      </div>

      {/* ── MESSAGES ───────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>

        {/* Suggested prompts */}
        {showSuggestions && messages.length <= 1 && (
          <div style={{ marginBottom: 16, animation: "fadein 0.4s ease" }}>
            <div style={{ fontSize: 7, color: "#374151", letterSpacing: 2, marginBottom: 8 }}>QUICK QUESTIONS</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
              {SUGGESTED_PROMPTS.map((p, i) => (
                <button key={i} onClick={() => handleSuggestion(p.text)}
                  style={{
                    padding: "8px 8px", borderRadius: 8, cursor: "pointer", textAlign: "left",
                    background: "#0a0d12", border: "1px solid #1e2530", transition: "all 0.2s",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#FFCD1140"; e.currentTarget.style.background = "#0d1117"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e2530"; e.currentTarget.style.background = "#0a0d12"; }}>
                  <div style={{ fontSize: 12, marginBottom: 3 }}>{p.icon}</div>
                  <div style={{ fontSize: 7, color: "#9ca3af", fontWeight: 600, lineHeight: 1.4 }}>{p.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} isLatest={i === messages.length - 1} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* ── FILE ATTACHMENTS PREVIEW ───────────────────────────────────── */}
      {attachedFiles.length > 0 && (
        <div style={{ padding: "6px 14px 0", borderTop: "1px solid #0d0d0d", flexShrink: 0 }}>
          {attachedFiles.map((f, i) => (
            <FilePreview key={i} file={f} onRemove={() => setAttachedFiles(prev => prev.filter((_, j) => j !== i))} />
          ))}
        </div>
      )}

      {/* ── WAVEFORM ──────────────────────────────────────────────────── */}
      <div style={{ padding: "0 14px", flexShrink: 0, borderTop: "1px solid #0d0d0d", background: "#070a0f" }}>
        <ChatWaveform isRecording={isRecording} isListening={false} audioLevel={audioLevel} />
      </div>

      {/* ── INPUT BAR ──────────────────────────────────────────────────── */}
      <div style={{ padding: "8px 14px 12px", flexShrink: 0, background: "#070a0f" }}>
        {/* Typing indicator hint */}
        {isRecording && (
          <div style={{ fontSize: 7, color: "#ef4444", letterSpacing: 1, marginBottom: 5, animation: "blink 1s infinite" }}>
            ● RECORDING — speak your question, tap mic to stop
          </div>
        )}

        <div style={{
          display: "flex", gap: 6, alignItems: "flex-end",
          background: "#0a0d12", borderRadius: 12,
          border: `1px solid ${inputFocused ? "#FFCD1140" : "#1e2530"}`,
          padding: "6px 6px 6px 10px",
          transition: "border-color 0.2s, box-shadow 0.2s",
          boxShadow: inputFocused ? "0 0 0 2px #FFCD1110" : "none",
        }}>
          {/* File attach button */}
          <label title="Attach image or PDF"
            style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 6, background: "#0d1117", border: "1px solid #1e2530", flexShrink: 0, transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#374151"; e.currentTarget.style.background = "#141414"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e2530"; e.currentTarget.style.background = "#0d1117"; }}>
            <span style={{ fontSize: 12 }}>📎</span>
            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf,.txt,.csv" onChange={handleFileAttach} style={{ display: "none" }} />
          </label>

          {/* Text input */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Ask about this inspection... (or tap 🎤)"
            rows={1}
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "#e2e8f0", fontSize: 9, fontFamily: "IBM Plex Mono", resize: "none",
              lineHeight: 1.6, padding: "4px 0", maxHeight: 80, overflowY: "auto",
            }}
          />

          {/* Mic button */}
          <button
            onClick={isRecording ? stopVoiceRecord : startVoiceRecord}
            style={{
              width: 32, height: 32, borderRadius: 8, border: "none", flexShrink: 0,
              background: isRecording ? "#ef4444" : "#141414",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
              boxShadow: isRecording ? "0 0 12px rgba(239,68,68,0.5)" : "none",
              animation: isRecording ? "pulse 1.5s infinite" : "none",
            }}
            onMouseEnter={e => { if (!isRecording) { e.currentTarget.style.background = "#1e2530"; } }}
            onMouseLeave={e => { if (!isRecording) { e.currentTarget.style.background = "#141414"; } }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill={isRecording ? "#fff" : "#6b7280"} stroke="none">
              <rect x="9" y="2" width="6" height="11" rx="3"/>
              <path d="M5 10a7 7 0 0014 0" stroke={isRecording ? "#fff" : "#6b7280"} fill="none" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="19" x2="12" y2="22" stroke={isRecording ? "#fff" : "#6b7280"} strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="22" x2="16" y2="22" stroke={isRecording ? "#fff" : "#6b7280"} strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Send button */}
          <button
            onClick={() => sendMessage()}
            disabled={loading || (!input.trim() && attachedFiles.length === 0)}
            style={{
              width: 32, height: 32, borderRadius: 8, border: "none", flexShrink: 0,
              background: (input.trim() || attachedFiles.length > 0) && !loading ? catYellow : "#141414",
              cursor: (input.trim() || attachedFiles.length > 0) && !loading ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s",
              boxShadow: (input.trim() || attachedFiles.length > 0) && !loading ? `0 0 10px ${catYellow}40` : "none",
            }}
            onMouseEnter={e => { if (!loading && input.trim()) { e.currentTarget.style.transform = "scale(1.08)"; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}>
            {loading ? (
              <svg width="12" height="12" viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite" }}>
                <circle cx="12" cy="12" r="10" fill="none" stroke="#6b7280" strokeWidth="3" strokeDasharray="30 70" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={input.trim() || attachedFiles.length > 0 ? "#000" : "#374151"} strokeWidth="2.5" strokeLinecap="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            )}
          </button>
        </div>

        {/* Multimodal hints */}
        <div style={{ display: "flex", gap: 8, marginTop: 5, justifyContent: "center" }}>
          {["📎 image/PDF", "🎤 voice input", "↵ enter to send"].map((h, i) => (
            <span key={i} style={{ fontSize: 6, color: "#1e2530", letterSpacing: 0.5 }}>{h}</span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadein { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
        @keyframes blink { 50% { opacity:0.3; } }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
        textarea::-webkit-scrollbar { width: 2px; }
        textarea::-webkit-scrollbar-thumb { background: #1e2530; }
      `}</style>
    </div>
  );
}